import { ApiError, foldersApi, matricesApi } from '@/lib/api';
import { notesKeys } from '@/lib/notes-queries';
import {
  type CreateFolderInput,
  FOLDER_NAME_TAKEN,
  type MatrixFolder,
  type MoveMatrixToFolderResponse,
  type UpdateFolderInput,
} from '@mss_creator/shared';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Queries and mutations of the matrix folders of a company (issue #19,
 * `docs/specs/matrices.md` « Dossiers de matrices »). A folder carries
 * counters (matrices, open notes) and each matrix its `folderId`, so every
 * write refreshes both `['folders', companyId]` and `['matrices', companyId]`.
 */

export const foldersKey = (companyId: string) => ['folders', companyId] as const;

/** The folders of a company, alphabetical, with their counters. */
export function useFolders(companyId: string) {
  return useQuery({
    queryKey: foldersKey(companyId),
    queryFn: () => foldersApi.listForCompany(companyId),
  });
}

function refreshLists(queryClient: QueryClient, companyId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: foldersKey(companyId) }),
    queryClient.invalidateQueries({ queryKey: ['matrices', companyId] }),
  ]);
}

export function useCreateFolder(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFolderInput): Promise<MatrixFolder> =>
      foldersApi.create(companyId, input),
    onSuccess: () => refreshLists(queryClient, companyId),
  });
}

export function useUpdateFolder(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      folderId,
      input,
    }: { folderId: string; input: UpdateFolderInput }): Promise<MatrixFolder> =>
      foldersApi.update(folderId, input),
    onSuccess: () => refreshLists(queryClient, companyId),
  });
}

/** Deleting a folder frees its matrices (« Hors dossier ») and deletes its notes. */
export function useDeleteFolder(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (folderId: string): Promise<void> => foldersApi.remove(folderId),
    onSuccess: (_result, folderId) => {
      queryClient.removeQueries({ queryKey: notesKeys.folder(folderId) });
      return refreshLists(queryClient, companyId);
    },
  });
}

/** Puts a matrix in a folder, or back « Hors dossier » with `folderId: null`. */
export function useMoveMatrix(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      matrixId,
      folderId,
    }: { matrixId: string; folderId: string | null }): Promise<MoveMatrixToFolderResponse> =>
      matricesApi.moveToFolder(matrixId, folderId),
    onSuccess: () => refreshLists(queryClient, companyId),
  });
}

export const FOLDER_NAME_TAKEN_MESSAGE = 'Un dossier porte déjà ce nom.';
export const FOLDER_SAVE_ERROR_MESSAGE = "Le dossier n'a pas pu être enregistré. Réessayez.";

/** True when the API refused a name already used by another folder of the company. */
export function isFolderNameTaken(error: unknown): boolean {
  return error instanceof ApiError && error.code === FOLDER_NAME_TAKEN;
}
